/**
 * How to reach the person. Each number dials on the desk; a number on the do-not-call list says
 * so and does not offer to dial, and a wrong number is struck through rather than hidden, so
 * nobody adds it back.
 */
import { formatPhone, PHONE_KINDS, phoneKindLabel, type Contact } from "@tlcrm/shared";
import { useState } from "react";

import { ApiError, errorMessage } from "../../api/client";
import { useAddPhone, useUpdateContact, useUpdatePhone } from "../../api/hooks";
import { useMe } from "../../auth/session";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toasts";
import { DncTag, Select } from "../../components/ui";
import { dial } from "../../lib/dial";

export function PhonesCard({ contact }: { contact: Contact }) {
  const { can } = useMe();
  const toast = useToast();
  const add = useAddPhone(contact.id);
  const update = useUpdateContact(contact.id);
  const updatePhone = useUpdatePhone(contact.id);
  const [adding, setAdding] = useState(false);
  const [number, setNumber] = useState("");
  const [kind, setKind] = useState<(typeof PHONE_KINDS)[number][0]>("mobile");
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      await add.mutateAsync({ e164: number, kind });
      setAdding(false);
      setNumber("");
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (Object.values(err.fields)[0] ?? err.message) : errorMessage(err),
      );
    }
  };

  const setDoNotCall = async (value: boolean) => {
    try {
      await update.mutateAsync({ version: contact.version, doNotCall: value });
      toast(value ? "Marked do not call. Every number is on the list." : "Do not call removed.");
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  const makePrimary = async (phoneId: string) => {
    try {
      await updatePhone.mutateAsync({ phoneId, isPrimary: true });
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Phone</h3>
        {!adding && (
          <button className="btn btn-sm" onClick={() => setAdding(true)}>
            Add number
          </button>
        )}
      </div>
      <div className="card-body stack" style={{ gap: "var(--s-4)" }}>
        {contact.doNotCall && (
          <div className="banner banner-brick">
            <Icon name="ban" />
            <span>Asked not to be called. Calls to this person are a compliance violation.</span>
            {can("dnc:remove") && (
              <button className="btn btn-sm" onClick={() => void setDoNotCall(false)}>
                Remove
              </button>
            )}
          </div>
        )}
        <div className="phones">
          {contact.phones.length === 0 && (
            <span className="t3" style={{ fontSize: "var(--fs-sm)" }}>
              No numbers on file.
            </span>
          )}
          {contact.phones.map((p) => {
            const blocked = p.onDnc || contact.doNotCall;
            return (
              <div key={p.id} className="phone-row">
                <span className="kind">{phoneKindLabel(p.kind)}</span>
                <span className="num">
                  {p.wrongNumber ? <s>{formatPhone(p.e164)}</s> : formatPhone(p.e164)}
                </span>
                {p.isPrimary && <span className="tag">Primary</span>}
                {p.onDnc && <DncTag />}
                {p.wrongNumber && <span className="tag">Wrong number</span>}
                {!p.isPrimary && !p.wrongNumber && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => void makePrimary(p.id)}
                    style={{ marginLeft: "auto" }}
                  >
                    Make primary
                  </button>
                )}
                {!blocked && !p.wrongNumber && (
                  <button
                    className="btn btn-sm"
                    onClick={() => dial(p.e164)}
                    title="Dial on the desk"
                    style={p.isPrimary ? { marginLeft: "auto" } : undefined}
                  >
                    <Icon name="phone" /> Call
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {adding && (
          <form
            className="stack"
            style={{ gap: "var(--s-4)" }}
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: "var(--s-4)" }}>
              <input
                autoFocus
                className={`field mono${error ? " is-invalid" : ""}`}
                placeholder="(407) 555-0188"
                inputMode="tel"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                aria-label="New number"
              />
              <Select value={kind} onChange={(x) => setKind(x || "mobile")} options={PHONE_KINDS} />
            </div>
            {error && <div className="field-error">{error}</div>}
            <div style={{ display: "flex", gap: "var(--s-4)", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!number.trim() || add.isPending}
              >
                Add
              </button>
            </div>
          </form>
        )}
        {!contact.doNotCall && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ justifySelf: "start" }}
            onClick={() => void setDoNotCall(true)}
          >
            <Icon name="ban" /> They asked not to be called
          </button>
        )}
      </div>
    </div>
  );
}
